import { LoadingOutlined } from '@ant-design/icons';
import { Input, InputNumber } from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { localizeNumber } from '../../library/utilities/intl.js';

const Currency = ({ value = null, currency = 'EUR', symbol = null, precision = 3, prefix, onChange, name = undefined, loading, ...otherProps }) => {

    const internalPrefix = useMemo(() => {
        let _prefix = ''
        if (symbol) {
            _prefix = symbol
        }
        else {
            switch (currency) {
                case 'EUR':
                    _prefix = '€'
                    break

                case 'GBP':
                    _prefix = '£'
                    break

                case 'USD':
                    _prefix = '$'
                    break
            }
        }
        return !prefix ? _prefix : `${_prefix}\u00A0\u00A0${prefix}`

    }, [currency, symbol, prefix])

    const [internalVal, setInternalVal] = React.useState(value);

    React.useEffect(() => {
        setInternalVal(value);
    }, [value]);

    if (loading) {
        return (
            <Input
                style={{ width: '90%', maxWidth: '10rem', minWidth: '4rem' }}
                {...otherProps}
                className={`always-visible currency-input${prefix ? ' has-prefix ' : ''} ${otherProps.className || ''}`}
                prefix={<>{internalPrefix}</>}
                suffix={<LoadingOutlined />}
                precision={precision}
                disabled
            />
        )
    }

    return (
        <>
            <InputNumber
                value={value !== undefined && value !== null ? value : internalVal ?? undefined}
                decimalSeparator={currency === 'EUR' ? ',' : '.'}
                placeholder={currency === 'EUR' ? '0,00' : '0.00'}
                style={{ width: '90%', maxWidth: '10rem', minWidth: '4rem' }}
                min={0}
                controls={false}
                {...otherProps}
                className={`currency-input${prefix ? ' has-prefix ' : ''} ${otherProps.className || ''}`}
                formatter={(val, info) => formatter(val, currency, precision, info)}
                prefix={<>{internalPrefix}</>}
                parser={(v) => parser(v, currency)}
                onChange={(v) => {
                    setInternalVal(v);
                    onChange?.(v);
                }}
                onFocus={event => event.target.select()}
                max={'999999999999999999999'}
                precision={precision}
            />
            {!!name && <input type="hidden" name={name} value={value ?? internalVal ?? ''} />}
        </>
    )
}

export const formatter = (val, currency, precision, info) => {
    if (info?.userTyping) {
        return info.input;
    }
    if (val == null || val === '') return '';
    return localizeNumber(
        val,
        currency === 'EUR' ? 'nl-NL' : 'en-US',
        precision
    )
}

export const parser = (val, _currency) => {
    if (val == null || val === '') return '';
    if (typeof val === 'number') return val;

    let str = val.toString().trim().replace(/[^\d.,-]/g, '');
    if (!str) return '';

    const hasDot = str.includes('.');
    const hasComma = str.includes(',');

    if (hasDot && hasComma) {
        const lastDot = str.lastIndexOf('.');
        const lastComma = str.lastIndexOf(',');
        if (lastComma > lastDot) {
            // EU format: 1.234,56 -> 1234.56
            str = str.replace(/\./g, '').replace(',', '.');
        } else {
            // US format: 1,234.56 -> 1234.56
            str = str.replace(/,/g, '');
        }
    } else if (hasComma) {
        str = str.replace(/,/g, '.');
    }

    return str;
}

Currency.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onChange: PropTypes.func,
    currency: PropTypes.oneOf(['EUR', 'USD', 'GBP']).isRequired,
    symbol: PropTypes.oneOf(['€', '£', '$']),
    precision: PropTypes.number,
    prefix: PropTypes.node,
    name: PropTypes.string,
    loading: PropTypes.bool,
}

export default Currency;